import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Paper,
  Card,
  CardContent,
  Link,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import PasswordField from '../components/PasswordField';

const LoginPage: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [challengeUser, setChallengeUser] = useState<any>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const completeLogin = async (user: any) => {
    const session = await Auth.currentSession();
    const accessToken = session.getAccessToken().getJwtToken();
    localStorage.setItem('aws-token', accessToken);

    login({
      email: user.attributes?.email || user.challengeParam?.userAttributes?.email || email,
      name: user.attributes?.name || user.challengeParam?.userAttributes?.name || '',
      createdAt: new Date().toISOString(),
      userType: 'patient',
    });

    navigate('/dashboard');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      const user = await Auth.signIn(email, password);

      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setChallengeUser(user);
        setLoading(false);
        return;
      }

      await completeLogin(user);
    } catch (error: any) {
      if (error.code === 'PasswordResetRequiredException') {
        navigate('/forgot-password', { state: { email } });
        return;
      }
      setErrorMessage(error.message || 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    if (newPassword !== confirmNewPassword) {
      setErrorMessage('Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters');
      setLoading(false);
      return;
    }

    try {
      const user = await Auth.completeNewPassword(challengeUser, newPassword);
      await completeLogin(user);
    } catch (error: any) {
      setErrorMessage(error.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  if (challengeUser) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ my: 4 }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom align="center">
              Set Your Password
            </Typography>
            <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
              For security, please create a new password for your account.
            </Typography>

            {errorMessage && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            <Box component="form" onSubmit={handleNewPasswordSubmit} noValidate>
              <PasswordField
                margin="normal"
                required
                fullWidth
                name="newPassword"
                label="New Password"
                id="newPassword"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoFocus
              />
              <PasswordField
                margin="normal"
                required
                fullWidth
                name="confirmNewPassword"
                label="Confirm New Password"
                id="confirmNewPassword"
                autoComplete="new-password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Password must be at least 8 characters with uppercase, lowercase, and numbers.
              </Typography>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? 'Setting password...' : 'Set Password & Sign In'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ my: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Patient Portal Log In
          </Typography>
          
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <PasswordField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                Forgot your password?
              </Link>
            </Box>
          </Box>
          
          <Card sx={{ mt: 3, backgroundColor: '#f5f5f5' }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" align="center">
                <strong>Account Access Notice:</strong> Your account will be created after payment or first sign-up for services. 
                Account credentials will be sent to the email address you register with to gain access.
              </Typography>
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
